import { createSelector } from 'reselect';
import get from 'lodash/get';

import { makeSelectorFactory } from '../../utils/redux/selectAdapter';
import {
  SCHEDULE_TYPES,
  SCHEDULE_VIEW_MODE,
} from '../../components/ProviderCalendar/constants';
import { COLOR_MAP } from '../../components/ProviderCalendar/utils/colorCodes';
import {
  selectManagePendingRequestSending,
  selectStatusChangesPending,
} from '../healthboard/selectors';

export const selectRootState = state => get(state, 'schedule');
const getSelectors = makeSelectorFactory(selectRootState);

/**
 *  PRACTICE PROVIDERS
 * */
export const {
  selectStatePending: selectPracticeProvidersPending,
  selectStateList: selectPracticeProviders,
} = getSelectors('practiceProviders');

/**
 *  SCHEDULE PROVIDER LOCATIONS
 * */
export const {
  selectStatePending: selectScheduleLocationPending,
  selectStateList: selectScheduleLocations,
} = getSelectors('providerLocations');

/**
 *  SCHEDULE PROVIDER SERVICES
 * */
export const {
  selectStatePending: selectScheduleServicesPending,
  selectStateList: selectScheduleServices,
} = getSelectors('providerServices');

/**
 *  SCHEDULE PATIENT TYPES
 * */
export const {
  selectStatePending: selectSchedulePatientTypesPending,
  selectStateList: selectSchedulePatientTypes,
} = getSelectors('patientTypes');

/**
 *  WORK SCHEDULE INSERT
 * */
export const {
  selectStatePending: selectScheduleInsertPending,
  selectStateSucceed: selectScheduleInsertSucceed,
} = getSelectors('workslotInsert');

/**
 *  BLOCK SCHEDULE INSERT
 * */
export const {
  selectStatePending: selectBlockScheduleInsertPending,
  selectStateSucceed: selectBlockScheduleInsertSucceed,
} = getSelectors('blockslotInsert');

/**
 *  BLOCK SCHEDULE INSERT
 * */
export const {
  selectStatePending: selectIntersectionsFindPending,
} = getSelectors('intersections');

/**
 *  BLOCK SCHEDULE REMOVE
 * */
export const {
  selectStatePending: selectBlockSlotRemovePending,
} = getSelectors('blockslotRemove');
/**
 *  BLOCK SCHEDULE UPDATE
 * */
export const {
  selectStatePending: selectBlockSlotUpdatePending,
} = getSelectors('blockslotUpdate');

/**
 *  WORK SCHEDULE REMOVE
 * */
export const { selectStatePending: selectWorkSlotRemovePending } = getSelectors(
  'workslotRemove',
);

/**
 *  WORK SCHEDULE UPDATE
 * */
export const { selectStatePending: selectWorkSlotUpdatePending } = getSelectors(
  'workslotUpdate',
);

/**
 *  SCHEDULE SEARCH
 * */
const selectScheduleListState = createSelector(
  selectRootState,
  state => get(state, 'scheduleList'),
);
export const selectScheduleListPending = createSelector(
  selectScheduleListState,
  state => get(state, 'sending'),
);
export const selectScheduleWorkSlots = createSelector(
  selectScheduleListState,
  state => get(state, 'workSlots', []),
);
export const selectScheduleBlockSlots = createSelector(
  selectScheduleListState,
  state => get(state, 'blockedSlots', []),
);
export const selectScheduleAppointments = createSelector(
  selectScheduleListState,
  state => get(state, 'appointments', []),
);
export const selectUTCOffsetSeconds = createSelector(
  selectScheduleListState,
  state => get(state, 'timeZone.utcOffsetSeconds', 0),
);

/**
 *  SELECTED POPOVER
 * */
const selectPopoverState = createSelector(
  selectRootState,
  state => get(state, 'selectedPopover'),
);
export const selectSchedulePopoverVisibility = createSelector(
  selectPopoverState,
  state => get(state, 'visible'),
);
export const selectSchedulePopoverSelectable = createSelector(
  selectPopoverState,
  state => get(state, 'selectable'),
);
export const selectSchedulePopoverPosition = createSelector(
  selectPopoverState,
  state => get(state, 'bounds'),
);
export const selectScheduleAreaPosition = createSelector(
  selectPopoverState,
  state => get(state, 'areaBounds', {}),
);
export const selectPopoverActiveAppointmentId = createSelector(
  selectPopoverState,
  state => get(state, 'appointmentId'),
);

export const selectSchedulePopoverData = createSelector(
  selectPopoverState,
  state => get(state, 'schedule'),
);
export const selectSchedulePopoverContentType = createSelector(
  selectPopoverState,
  state => get(state, 'type'),
);
export const selectSchedulePopoverDataId = createSelector(
  selectSchedulePopoverData,
  schedule => get(schedule, 'id'),
);
export const selectSchedulePopoverColor = createSelector(
  selectSchedulePopoverData,
  schedule => {
    const isBlocked = get(schedule, '__type') === SCHEDULE_TYPES.BLOCK;

    return isBlocked ? COLOR_MAP.SILVER : get(schedule, 'colorMarkerHex');
  },
);

/**
 *  SCHEDULE FILTER
 * */
const selectScheduleFilterState = createSelector(
  selectRootState,
  state => get(state, 'filter'),
);
export const selectScheduleDateRange = createSelector(
  selectScheduleFilterState,
  state => ({
    startOn: get(state, 'startOn'),
    endOn: get(state, 'endOn'),
  }),
);
export const selectScheduleFilterDate = createSelector(
  selectScheduleFilterState,
  state => get(state, 'date'),
);

/**
 *  SCHEDULE VALIDATION
 * */
const selectScheduleValidateState = createSelector(
  selectRootState,
  state => get(state, 'validate'),
);
export const selectScheduleValidating = createSelector(
  selectScheduleValidateState,
  state => get(state, 'validating'),
);
export const selectScheduleTargerProviderId = createSelector(
  selectScheduleValidateState,
  state => get(state, 'providerId'),
);
export const selectScheduleTargerProviderUserId = createSelector(
  selectScheduleValidateState,
  state => get(state, 'userId'),
);
export const selectScheduleOverlapModalVisibility = createSelector(
  selectScheduleValidateState,
  state => get(state, 'overlapModal'),
);
export const selectScheduleOverlapAppointmentVisibility = createSelector(
  selectScheduleValidateState,
  state => get(state, 'overlapAppointment'),
);
export const selectScheduleReursionModalVisibility = createSelector(
  selectScheduleValidateState,
  state => get(state, 'recursionModal.opened', false),
);
export const selectScheduleReursionModalType = createSelector(
  selectScheduleValidateState,
  state => get(state, 'recursionModal.type'),
);
export const selectScheduleReursionModal = createSelector(
  selectScheduleValidateState,
  state => get(state, 'recursionModal', {}),
);
export const selectScheduleAdminAccess = createSelector(
  selectScheduleValidateState,
  state => get(state, 'scheduleAdmin'),
);
export const selectScheduleViewMode = createSelector(
  selectScheduleValidateState,
  state => get(state, 'viewMode'),
);
export const selectIsEditMode = createSelector(
  selectScheduleViewMode,
  mode => mode === SCHEDULE_VIEW_MODE.EDIT,
);

// appointmentGuard
/**
 *  APPOINTMENT GUARD
 * */
const selectAppointmentGuardState = createSelector(
  selectRootState,
  state => get(state, 'appointmentGuard'),
);

export const selectAppointmentGuardPending = createSelector(
  selectAppointmentGuardState,
  state => get(state, 'pending'),
);
export const selectAppointmentGuardCanCreate = createSelector(
  selectAppointmentGuardState,
  state => get(state, 'canCreate'),
);

export const selectGlobalCalendarLoading = createSelector(
  selectScheduleInsertPending,
  selectBlockScheduleInsertPending,
  selectIntersectionsFindPending,
  selectScheduleListPending,
  selectWorkSlotRemovePending,
  selectWorkSlotUpdatePending,
  selectBlockSlotRemovePending,
  selectBlockSlotUpdatePending,
  selectManagePendingRequestSending,
  selectStatusChangesPending,
  (...loading) => loading.some(pending => Boolean(pending)),
);

/**
 *  SCHEDULE DEFAULTS (predefined start, end time)
 * */
export const {
  selectStatePending: selectScheduleDefaultsPending,
  selectStateData: selectScheduleDefaultsData,
} = getSelectors('scheduleDefaults');

/**
 *  VISIT TYPES BY LOCATION
 * */
export const {
  selectStatePending: selectVisitTypesPending,
  selectStateList: selectVisitTypes,
} = getSelectors('visitTypes');

/**
 *  PATIENTS BY PROVIDER
 * */
export const {
  selectStatePending: selectPatientListPending,
  selectStateList: selectPatientList,
} = getSelectors('patientList');

/**
 *  CREATE APPOINTMENT
 * */
export const {
  selectStatePending: selectCreateAppointmentPending,
} = getSelectors('createAppointment');
/**
 *  INSPECT APPOINTMENT
 * */
export const {
  selectStatePending: selectInspectAppointmentPending,
} = getSelectors('inspectAppointment');



// WEBPACK FOOTER //
// ./src/redux/schedule/selectors.js